import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, ModalController, NavController } from '@ionic/angular';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { WorkingHoursComponent } from 'src/app/components/working-hours/working-hours.component';
import { DEFAULT_INITIAL_SUPPORT_STEP, EXTERNAL_CHAT_CONNECTION_TYPES, FEATURES, routes } from 'src/app/constants';
import { environment } from 'src/environments/environment';
import { Customer, ExternalChatConnection, Profile } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { ExternalChatConnectionsService } from 'src/services/external-chat-connections.service';
import { MessageService } from 'src/services/message.service';
import { UsersService } from 'src/services/users.service';

@Component({
  selector: 'app-admin-microsoft-teams',
  templateUrl: './admin-microsoft-teams.component.html',
  styleUrls: ['./admin-microsoft-teams.component.scss'],
})
export class AdminMicrosoftTeamsComponent implements OnInit {
  forwardEmail: string;
  externalChatConnections: Partial<ExternalChatConnection>[] | null;
  hasMicrosoftTeamsConnections = false;
  featureEnabled = true;
  showingIndex = -1;
  newAllowlistTeam = '';
  newAllowlistChannel = '';
  projectId: string;
  connectedUsers: Partial<Profile>[] = [];

  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private customersService: CustomersService,
    private externalChatConnectionsService: ExternalChatConnectionsService,
    private usersService: UsersService,
    private navController: NavController,
    private apiService: ApiService,
    private route: ActivatedRoute,
  ) {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  async ngOnInit() {
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.EXTERNAL_CHAT_CONNECTIONS);

    await this.getMicrosoftTeamsConnections();
  }

  addConnection() {
    this.externalChatConnections.push({
      supportSteps: [DEFAULT_INITIAL_SUPPORT_STEP],
      name: undefined,
      type: EXTERNAL_CHAT_CONNECTION_TYPES.MICROSOFT_TEAMS,
      channelId: '',
      autoresponder: { enabled: false, condition: 'always', message: '' },
      channelAllowlistEnabled: false,
      channelAllowlist: [],
      enabled: true,
      projectId: this.projectId,
    });
    this.showingIndex = this.externalChatConnections.length - 1;
  }

  async getMicrosoftTeamsConnections() {
    const result = await this.externalChatConnectionsService.list({
      order: ['createdAt'],
      descending: false,
      where: [
        {
          field: 'type',
          operator: '==',
          value: EXTERNAL_CHAT_CONNECTION_TYPES.MICROSOFT_TEAMS,
        },
        {
          field: 'projectId',
          operator: '==',
          value: this.projectId,
        },
      ],
    });

    this.externalChatConnections = result.data ?? null;
    this.hasMicrosoftTeamsConnections = !!this.externalChatConnections.length;

    if (this.hasMicrosoftTeamsConnections) {
      this.getCustomers();
    } else {
      this.addConnection();
    }

    await this.getConnectedUsers();
  }

  async getCustomers() {
    if (this.externalChatConnections?.length) {
      this.externalChatConnections.forEach(async (connection) => {
        const customer: Partial<Customer> = await this.customersService.get(connection.customerId);

        connection.name = customer.name;
        connection.supportSteps = customer.supportSteps;
      });
    }
  }

  async getConnectedUsers() {
    const connectedUserIds = this.externalChatConnections?.flatMap(
      (connection) => connection.providerConfig?.connectedUserIds ?? [],
    );
    const users = connectedUserIds.length
      ? await this.usersService.list({
          where: [{ field: 'id', operator: 'in', value: connectedUserIds }],
        })
      : { data: [] };

    this.connectedUsers = users.data ?? [];
  }

  getConnectedUsersForConnection(index: number) {
    const connection = this.externalChatConnections[index];

    return connection.providerConfig?.connectedUserIds
      ?.map((id) => this.connectedUsers.find((user) => user.id === id))
      .filter(Boolean);
  }

  isAlreadyConnected() {
    return this.connectedUsers.some((user) => user.id === this.authService.userId);
  }

  async addConnectedUser(index: number) {
    const connection = this.externalChatConnections[index];

    await this.microsoftTeamsOauth(connection.oauthId);
    await this.externalChatConnectionsService.addMicrosoftTeamsConnection(connection.id);

    connection.providerConfig.connectedUserIds.push(this.authService.userId);

    await this.getMicrosoftTeamsConnections();
  }

  async editWorkingHours() {
    const modal = await this.modalCtrl.create({
      component: WorkingHoursComponent,
      componentProps: {
        originalWorkingHours: this.authService.tenant?.workingHours ?? {},
        onSave: async (updates: any) => this.authService.updateTenant(updates),
      },
    });

    modal.present();
  }

  async save(index: number) {
    try {
      const payload: Partial<ExternalChatConnection> = this.externalChatConnections[index];

      if (!payload?.id) {
        const oauthId = await this.microsoftTeamsOauth();
        const connection = await this.externalChatConnectionsService.create({
          ...payload,
          oauthId,
        });

        await this.externalChatConnectionsService.addMicrosoftTeamsConnection(connection.id);
      } else {
        await this.externalChatConnectionsService.update(payload.id, payload);
      }

      this.getMicrosoftTeamsConnections();

      this.msgSrvc.show('✔︎ Saved');
      this.showingIndex = -1;
    } catch (e) {
      this.msgSrvc.showError(e);
    }
  }

  supportStepsDidChange(supportSteps: any[], index: number) {
    this.externalChatConnections[index].supportSteps = supportSteps;
  }

  async delete(index: number) {
    if (!this.externalChatConnections[index].id) {
      this.externalChatConnections.pop();
      return;
    }

    const alert = await this.alertCtrl.create({
      header: 'Disable email forwarding',
      subHeader: 'Are you sure you want to disable this chat connection?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes',
          handler: async () => {
            if (this.externalChatConnections[index]) {
              await this.externalChatConnectionsService.delete(this.externalChatConnections[index].id);

              this.getMicrosoftTeamsConnections();
            }
          },
        },
      ],
    });

    await alert.present();
  }

  showMicrosoftTeamsHelpdesk() {
    window.open('https://docs.unthread.io/integrations/microsoft-teams', '_blank');
  }

  toggleShowing(index: number) {
    const connection = this.externalChatConnections[index];

    if (!connection?.id && this.hasMicrosoftTeamsConnections) {
      this.externalChatConnections.pop();
      return;
    }

    if (this.showingIndex === index) {
      this.showingIndex = -1;
    } else {
      this.showingIndex = index;
    }
  }

  viewInbox(connection: ExternalChatConnection) {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${connection.type}/${connection.id}`);
  }

  onConnectionChannelChanged(connection, slackChannel, index: number) {
    connection.channelId = slackChannel?.id ?? null;
    connection.slackChannel = slackChannel;

    if (connection.id) {
      this.save(index);
    }
  }

  autoresponderDidChange(connection: ExternalChatConnection, autoresponder: any) {
    connection.autoresponder = autoresponder;
  }

  async microsoftTeamsOauth(oauthId = uuidv4()) {
    const Nango = (await import('@nangohq/frontend')).default;
    const nango = new Nango({ publicKey: environment.nangoPublicKey });
    const { hmac } = (await this.apiService.postPromise(`/integrations/pre-register`, {
      integration: EXTERNAL_CHAT_CONNECTION_TYPES.MICROSOFT_TEAMS,
      connectionParams: [oauthId, this.authService.userId],
    })) as any;

    const options = {
      hmac,
      params: {},
      authorization_params: { prompt: '' },
    };

    try {
      await nango.auth(
        EXTERNAL_CHAT_CONNECTION_TYPES.MICROSOFT_TEAMS,
        `${this.authService.tenant.id}-${oauthId}-${this.authService.userId}`,
        options,
      );
    } catch (err: any) {
      throw new Error(err.message);
    }

    return oauthId;
  }

  addToAllowlist(connection: ExternalChatConnection) {
    if (this.newAllowlistTeam && this.newAllowlistChannel) {
      if (!connection.channelAllowlist) {
        connection.channelAllowlist = [];
      }

      connection.channelAllowlist.push(`${this.newAllowlistTeam.trim()}:${this.newAllowlistChannel.trim()}`);

      this.newAllowlistTeam = '';
      this.newAllowlistChannel = '';
    }
  }

  removeFromAllowlist(connection: ExternalChatConnection, index: number) {
    connection.channelAllowlist.splice(index, 1);
  }

  trackById(user: Profile) {
    return user.id;
  }
}
